// Ordnet die Stellenangabe einer Bewerbung den Stellenanzeigen aus dem CMS zu.
//
// Zwischen beiden gibt es keine Verknüpfung, nur Text: submissions.job_title
// entsteht beim Absenden aus der Matrix-Frage und sieht aus wie
// "Verkauf · Vollzeit", bei Mehrfachbewerbung "Verkauf · Vollzeit, Backstage · Teilzeit".
// Die Anzeige im CMS trägt title und role, beides Freitext von Hand.
//
// Deshalb rät diese Datei sichtbar statt heimlich: sie sortiert und begründet,
// entscheiden tut der Mensch im Dialog. Keine Stelle fällt je aus der Liste.

const UMLAUTS = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };

// (m/w/d), (w/m/d), (m/w/x), (all genders) — steht in fast jedem Titel und
// unterscheidet zwei Stellen nie voneinander.
const GENDER_SUFFIX = /\((?:[mwdx]\s*\/\s*)+[mwdx]\)|\(all genders\)/gi;

/** Eine Schreibweise für den Vergleich: klein, ohne Umlaute, ohne Zierrat. */
export const normalizeJobText = (raw) => {
  const lower = String(raw || '').toLowerCase();
  const withoutGender = lower.replace(GENDER_SUFFIX, ' ');
  const spelled = withoutGender.replace(/[äöüß]/g, (char) => UMLAUTS[char]);
  const ascii = spelled.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return ascii.replace(/[^a-z0-9]+/g, ' ').trim();
};

/**
 * Die einzelnen Bewerbungen aus job_title.
 *
 * Getrennt wird am Komma, weil getJobTitleEntries die Einträge so verkettet.
 * Ein Stellentitel mit Komma darin zerfällt dadurch — das kostet höchstens
 * eine Vorauswahl, nie eine Stelle in der Liste.
 */
export const parseJobTitleField = (jobTitle) =>
  String(jobTitle || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [stelle, ...rest] = entry.split('·').map((part) => part.trim());
      return { stelle, modell: rest.join(' · ') };
    })
    .filter((entry) => entry.stelle);

const tokensOf = (text) => new Set(normalizeJobText(text).split(' ').filter(Boolean));

/** Anteil gemeinsamer Wörter, gemessen an der kürzeren der beiden Angaben. */
const overlap = (a, b) => {
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  a.forEach((word) => {
    if (b.has(word)) shared += 1;
  });
  return shared / Math.min(a.size, b.size);
};

/** Ab hier ist eine Überschneidung mehr als Zufall. */
const PARTIAL_THRESHOLD = 0.5;

const ORDER = { exact: 0, partial: 1, none: 2 };

/** Wie gut eine Anzeige zu einer einzelnen Angabe des Bewerbers passt. */
function scoreJob(entry, job) {
  const stelle = normalizeJobText(entry.stelle);
  let best = { match: 'none', score: 0 };
  for (const field of [job?.title, job?.role].filter(Boolean)) {
    if (normalizeJobText(field) === stelle && stelle) return { match: 'exact', score: 1 };
    const ratio = overlap(tokensOf(entry.stelle), tokensOf(field));
    if (ratio >= PARTIAL_THRESHOLD && ratio > best.score) best = { match: 'partial', score: ratio };
  }
  return best;
}

/**
 * Alle Stellenanzeigen, sortiert: Volltreffer, Teiltreffer, Rest.
 *
 * Das Arbeitsmodell (Vollzeit/Teilzeit) bleibt außen vor — es verfeinert
 * nichts, was der Titel nicht schon sagt, und dürfte niemals etwas ausschließen.
 */
export function rankJobListings(jobTitle, jobs = []) {
  const entries = parseJobTitleField(jobTitle);
  return [...jobs]
    .map((job) => {
      let best = { match: 'none', score: 0 };
      for (const entry of entries) {
        const result = scoreJob(entry, job);
        const better =
          ORDER[result.match] < ORDER[best.match] ||
          (result.match === best.match && result.score > best.score);
        if (better) best = result;
      }
      return { job, match: best.match, score: best.score };
    })
    .sort(
      (a, b) =>
        ORDER[a.match] - ORDER[b.match] ||
        b.score - a.score ||
        String(a.job.title || '').localeCompare(String(b.job.title || ''), 'de')
    );
}

/**
 * Die Stelle, die der Dialog vorauswählt: der beste Treffer, für den auch ein
 * Fragebogen 2 hinterlegt ist. Eine Stelle ohne Fragebogen wird nie
 * vorausgewählt — der Senden-Knopf wäre sonst eine Sackgasse.
 */
export function pickPreselection(ranked = [], jobIdsWithAssessment = new Set()) {
  const hit = ranked.find(
    (entry) => entry.match !== 'none' && jobIdsWithAssessment.has(String(entry.job.id))
  );
  return hit ? hit.job : null;
}
